/**
 * §1.4 — the Company World drill-down, reached at `/#/pillar/{code}`.
 *
 * Three required panels:
 *  1. Projects grouped by the §4.5 status matrix, collapsed except
 *     `active_fieldwork`, sortable, exportable to CSV.
 *  2. Recent Daily Logs, last 14 days, with the photo strip.
 *  3. The Field Media Injection Stream — pending `media_inbox` items.
 *
 * The scope is set on the server before this view is entered (§1.4 step 1),
 * so nothing here filters by pillar; it renders what the scoped response
 * returned. That is the point of a server-enforced scope: the screen has no
 * ability to widen it.
 */
import { useEffect, useState } from 'react';
import type { CompanyWorld as World, PillarButton, WorldProjectRow } from '../lib/api';
import { api } from '../lib/api';
import { DailyLogFeed } from './DailyLogFeed';
import { ProcurementTable } from './ProcurementTable';
import { money, statusLabel, FLAG_LABEL } from '../lib/status';

interface Props {
  code: string;
  identity: PillarButton | undefined;
  onOpenProject?: (id: string) => void;
}

type SortKey = 'code' | 'name' | 'clientAccountName' | 'percentComplete';

export function CompanyWorld({ code, identity, onOpenProject }: Props): JSX.Element {
  const [world, setWorld] = useState<World | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: 'code', asc: true });

  useEffect(() => {
    let live = true;
    setWorld(null);
    setError(null);
    void (async () => {
      try {
        const next = await api.companyWorld(code as never);
        if (!live) return;
        setWorld(next);
        setOpen(
          Object.fromEntries(next.statusGroups.map((g) => [g.status, g.expandedByDefault])),
        );
      } catch {
        if (live) setError('This entity could not be opened with your current scope.');
      }
    })();
    return () => {
      live = false;
    };
  }, [code]);

  if (error) {
    return (
      <p role="status" className="text-body text-warning">
        {error}
      </p>
    );
  }

  if (!world) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-card bg-white" />
        ))}
      </div>
    );
  }

  const allProjects = world.statusGroups.flatMap((g) => g.projects);

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h1 className="text-h1 font-semibold text-text-primary">
          {identity?.displayName ?? code}
        </h1>
        <p className="text-body text-text-secondary">
          {identity?.legalName ?? ''}
          {identity?.microCopy ? ` · ${identity.microCopy}` : ''}
        </p>
      </header>

      {/* ------------------------------------------- Panel 1: projects */}
      <section aria-labelledby="world-projects">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 id="world-projects" className="text-h2 font-semibold text-text-primary">
            Projects
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-micro text-text-secondary" htmlFor="world-sort">
              Sort by
            </label>
            <select
              id="world-sort"
              value={`${sort.key}:${sort.asc ? 'asc' : 'desc'}`}
              onChange={(e) => {
                const [key, dir] = e.target.value.split(':');
                setSort({ key: key as SortKey, asc: dir === 'asc' });
              }}
              className="min-h-touch rounded-card border border-border bg-white px-2 text-body"
            >
              <option value="code:asc">Code</option>
              <option value="name:asc">Name</option>
              <option value="clientAccountName:asc">Client</option>
              <option value="percentComplete:desc">Percent complete</option>
            </select>
            <button
              type="button"
              onClick={() => exportCsv(code, allProjects)}
              disabled={allProjects.length === 0}
              className="btn-secondary rounded-card px-3 text-body font-medium disabled:opacity-50"
            >
              Export CSV
            </button>
          </div>
        </div>

        {allProjects.length === 0 ? (
          <div className="empty-state">
            <p className="text-body font-medium text-text-primary">No projects in this entity yet</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {world.statusGroups.map((group) => (
              <details
                key={group.status}
                open={open[group.status] ?? group.expandedByDefault}
                onToggle={(e) =>
                  setOpen((prev) => ({
                    ...prev,
                    [group.status]: (e.target as HTMLDetailsElement).open,
                  }))
                }
                className="card overflow-hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-body font-medium text-text-primary">
                  <span>{statusLabel(group.status)}</span>
                  <span className="numeric text-text-secondary">{group.count}</span>
                </summary>
                {group.count === 0 ? (
                  <p className="px-3 pb-3 text-body text-text-secondary">
                    No projects are in this status.
                  </p>
                ) : (
                  <div className="border-t border-border">
                    {/*
                      The row carries seven columns and a phone is 375px wide,
                      so on a small screen the table scrolls sideways inside
                      its own card rather than pushing the page out. A person
                      cannot swipe a column they do not know is there, so the
                      hint below is shown at that size instead of leaving the
                      contract-value, PM and flag columns silently cut off.
                    */}
                    <p className="px-3 pt-2 text-micro text-text-secondary md:hidden">
                      Swipe the table sideways for contract value, project manager and flags.
                    </p>
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse text-body">
                      <thead>
                        <tr className="table-header text-left">
                          <th scope="col" className="px-3 py-2 font-medium">Code</th>
                          <th scope="col" className="px-3 py-2 font-medium">Project</th>
                          <th scope="col" className="px-3 py-2 font-medium">Client</th>
                          <th scope="col" className="px-3 py-2 font-medium">Complete</th>
                          <th scope="col" className="px-3 py-2 font-medium">Contract value</th>
                          <th scope="col" className="px-3 py-2 font-medium">PM</th>
                          <th scope="col" className="px-3 py-2 font-medium">Flags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted(group.projects, sort).map((p) => (
                          <tr key={p.id} className="table-row align-top">
                            <td className="numeric px-3 py-2">
                              {onOpenProject ? (
                                <button
                                  type="button"
                                  onClick={() => onOpenProject(p.id)}
                                  className="min-h-0 min-w-0 border-0 bg-transparent p-0 text-left text-royal underline"
                                >
                                  {p.code}
                                </button>
                              ) : (
                                p.code
                              )}
                            </td>
                            <td className="px-3 py-2 text-text-primary">{p.name}</td>
                            <td className="px-3 py-2 text-text-secondary">{p.clientAccountName}</td>
                            <td className="px-3 py-2">
                              <span className="numeric">{p.percentComplete}%</span>
                              <span
                                aria-hidden="true"
                                className="mt-1 block h-1.5 w-20 overflow-hidden rounded-pill bg-surface"
                              >
                                <span
                                  className="block h-full bg-royal"
                                  style={{ width: `${Math.min(100, Math.max(0, p.percentComplete))}%` }}
                                />
                              </span>
                            </td>
                            <td className="numeric px-3 py-2">
                              {money(p.contractValue) ?? (
                                <span className="text-micro text-text-secondary">not recorded</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-text-secondary">
                              {p.pmName ?? <span className="text-micro">unassigned</span>}
                            </td>
                            <td className="px-3 py-2">
                              {p.flags.length === 0 ? (
                                <span className="text-micro text-text-secondary">none</span>
                              ) : (
                                <span className="flex flex-wrap gap-1">
                                  {p.flags.map((flag) => (
                                    <span
                                      key={flag}
                                      title={FLAG_LABEL[flag] ?? flag}
                                      className="inline-flex items-center gap-1 text-micro text-danger"
                                    >
                                      <span
                                        aria-hidden="true"
                                        className="block h-2 w-2 rounded-full bg-danger"
                                      />
                                      {FLAG_LABEL[flag] ?? flag}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
              </details>
            ))}
          </div>
        )}
      </section>

      {/* -------------------------------------- Panel 2: recent daily logs */}
      <section aria-labelledby="world-logs">
        <h2 id="world-logs" className="mb-2 text-h2 font-semibold text-text-primary">
          Recent daily logs
        </h2>
        <DailyLogFeed logs={world.dailyLogs.logs} days={world.dailyLogs.days} />
      </section>

      {/* ------------------------------------------ procurement, §4.6 */}
      <section aria-labelledby="world-procurement">
        <h2 id="world-procurement" className="mb-2 text-h2 font-semibold text-text-primary">
          Procurement
        </h2>
        <ProcurementTable items={world.procurement.items} atRisk={world.procurement.atRisk} />
      </section>

      {/* ------------------------------- Panel 3: field media injection */}
      <section aria-labelledby="world-media">
        <h2 id="world-media" className="mb-2 text-h2 font-semibold text-text-primary">
          Field media
        </h2>
        {world.mediaInbox.pending === 0 ? (
          <div className="empty-state">
            <p className="text-body font-medium text-text-primary">Nothing waiting to be filed</p>
            <p className="mt-1 text-body">
              Media sent in from the field lands here for a person to attach to a project. The
              intake channel is not connected yet, so this queue holds no items.
            </p>
          </div>
        ) : (
          <p className="text-body text-text-primary">
            <span className="numeric">{world.mediaInbox.pending}</span> item
            {world.mediaInbox.pending === 1 ? '' : 's'} waiting to be filed.
          </p>
        )}
      </section>
    </div>
  );
}

function sorted(rows: WorldProjectRow[], sort: { key: SortKey; asc: boolean }): WorldProjectRow[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    const x = a[sort.key];
    const y = b[sort.key];
    const cmp = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return sort.asc ? cmp : -cmp;
  });
  return copy;
}

/**
 * §1.4 Panel 1: "Export to CSV". A blank contract value is exported blank, for
 * the same reason it is displayed blank — a zero in a spreadsheet column is
 * indistinguishable from a measured zero once it leaves this screen.
 */
function exportCsv(code: string, rows: WorldProjectRow[]): void {
  const header = [
    'code',
    'name',
    'client',
    'status',
    'workstream',
    'percent_complete',
    'contract_value',
    'project_manager',
    'city',
    'state',
    'flags',
  ];
  const escape = (value: string): string => `"${value.replace(/"/g, '""')}"`;
  const lines = [
    header.join(','),
    ...rows.map((r) =>
      [
        r.code,
        r.name,
        r.clientAccountName,
        r.status,
        r.workstream,
        String(r.percentComplete),
        r.contractValue === null ? '' : String(r.contractValue),
        r.pmName ?? '',
        r.siteCity ?? '',
        r.siteState ?? '',
        r.flags.join(' '),
      ]
        .map(escape)
        .join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${code.toLowerCase()}-projects.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
